/**
 * Decisions at the project's home (D215 round 2): an `ask_human` a team raised, answerable from
 * the workbench, the console or the phone, with the asking machine told over the relay. A
 * decision is a project record (projectRecords.ts) under its repository (ids are `D-` plus three
 * random bytes, unique per team file); the workbench's Decision (src/coordination/decisions.ts)
 * is carried loosely with the checked fields below. AIOE's own contract; the workbench copies it
 * into `shared/schemas/decisions.ts`.
 *
 *   GET  /projects/v1/:id/decisions?status=open|answered&rootId=&limit=      members: across repositories; rootId "" is the project's own
 *   PUT  /projects/v1/:id/roots/:rootId/decisions/:did                        the asking workbench (contributors and above): create or replace while open
 *   GET  /projects/v1/:id/roots/:rootId/decisions/:did
 *   POST /projects/v1/:id/roots/:rootId/decisions/:did/answer  { answer }     contributors and above: status → answered; the asking device
 *                                                                             hears `decisions.answered { rootId, did, answer }`; the inbox record is done
 *
 * AIOE 0.21.0: the project's own decisions (rootId "", which no `/roots/:rootId/` path can carry) have
 * paths without the root segment, otherwise the same:
 *
 *   PUT  /projects/v1/:id/decisions/:did · GET /projects/v1/:id/decisions/:did · POST /projects/v1/:id/decisions/:did/answer { answer }
 *
 * An open decision files a needs-you inbox record to the person who asked (dedupeKey
 * `decision:<rootId>:<did>`), whose action answers it; the phone and the console use that.
 * Words for people: decision, question, answered, provisional (the default taken without a person).
 */
import { z } from "zod";
export const DECISIONS_SCHEMA_VERSION = 1;
export const DECISION_KINDS = ["single", "multi", "yesno", "text"];
export const decisionKindSchema = z.enum(DECISION_KINDS);
export const DECISIONS_ANSWERED_RPC = "decisions.answered";
/** The organisation action on a decision's inbox record: `POST …/act { actionId }` with `params.value` answers it. */
export const DECISION_ANSWER_ACTION_RPC = "aioe.decision.answer";
export const decisionDedupeKey = (rootId, did) => `decision:${rootId}:${did}`;
const id = z.string().min(1).max(200);
export const decisionAnswerSchema = z.object({
    value: z.union([z.string().max(20_000), z.array(z.string().max(200)).max(50)]),
    by: z.string().max(200).optional(),
    at: z.string().max(50).optional(),
    note: z.string().max(2000).optional(),
    /** The default was adopted without a person. */
    provisional: z.boolean().optional(),
});
export const decisionPutSchema = z.looseObject({
    id,
    teamId: z.string().min(1).max(200),
    /** The team's name for people (workbench D239), beside `teamId`; the inbox record carries it as `teamName`. */
    team: z.object({ id: z.string().min(1).max(200), name: z.string().max(200).optional() }).optional(),
    conversationId: z.string().max(200).optional(),
    taskId: z.string().max(100).optional(),
    planId: z.string().max(200).optional(),
    askedBy: z.string().max(200),
    askedAt: z.string().max(50),
    /**
     * The person the question is for (workbench B-14 / D236): the task's person when it is about a
     * person's task, else whoever asked. A workbench may send it; the platform fills it from the task's
     * assignee when absent, and the inbox record goes to this person and to the asker.
     */
    addressedTo: z.object({ id: z.string().min(1).max(200), name: z.string().max(200).optional() }).optional(),
    question: z.string().min(1).max(4000),
    why: z.string().max(4000).optional(),
    kind: decisionKindSchema,
    options: z.array(z.looseObject({ id: z.string().min(1).max(100), label: z.string().max(300), detail: z.string().max(2000).optional() })).max(8).default([]),
    default: z.union([z.string().max(200), z.array(z.string().max(200)).max(50)]).optional(),
    blocking: z.enum(["block", "proceed"]).default("block"),
    status: z.enum(["open", "answered"]).default("open"),
    answer: decisionAnswerSchema.optional(),
    tag: z.string().max(50).optional(),
});
export const decisionAnswerRequestSchema = z.object({ answer: decisionAnswerSchema });
//# sourceMappingURL=decisions.js.map